import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { camelize, decamelize } from 'humps'
import {
  Table,
  Column,
  Menu,
  Dropdown,
  Icon,
  Tooltip,
  Spin,
  RadioButton,
  RadioGroup,
} from './common/Ant'
import { Anchor, ActionAnchor } from './common/Anchor'
import SearchFilter from './common/SearchFilter'
import Select, { Option } from './common/Select'
import { toTitleCase } from '../utils/textFormatters'
import { hasPermission } from '../utils/hasPermission'
import { createQueryString, parseQueryParams } from '../utils/queryParams'
import {
  GATEWAY_STATUS,
  GATEWAY_TYPES,
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE,
  VIEW_ALL,
  CONNECTIVITY_STATUS,
  USER_PERMISSIONS,
} from '../constants'
import { configurePagination } from '../utils/helpers'

const parseSortFromQuery = sort => {
  if (!sort) {
    return {}
  }

  const desc = sort.startsWith('-')
  const field = camelize(desc ? sort.slice(1) : sort)

  return {
    field,
    order: desc ? 'descend' : 'ascend',
    columnKey: field,
  }
}

const buildSortQueryParam = sortedInfo => {
  if (!sortedInfo || !sortedInfo.field || !sortedInfo.order) {
    return undefined
  }

  const prefix = sortedInfo.order === 'descend' ? '-' : ''
  const field = decamelize(sortedInfo.field)

  return `${prefix}${field}`
}

const normalizeSorter = sorter => {
  if (!sorter) {
    return {}
  }

  const active = Array.isArray(sorter)
    ? sorter.find(column => column.order) || {}
    : sorter
  const field = active.field || active.columnKey

  if (!field || !active.order) {
    return {}
  }

  return {
    field,
    order: active.order,
    columnKey: field,
  }
}

const compareFieldValues = (a, b, field) => {
  const av = a[field] == null ? '' : String(a[field])
  const bv = b[field] == null ? '' : String(b[field])

  return av.localeCompare(bv)
}

class AllGateways extends Component {
  constructor(props) {
    super(props)

    this.state = {
      sortedInfo: {},
      searchText: '',
      page: DEFAULT_PAGE,
      perPage: DEFAULT_PER_PAGE,
      siteSlug: undefined,
      online: undefined,
      status: undefined,
      gatewayType: undefined,
    }
  }

  static propTypes = {
    gateways: PropTypes.arrayOf(PropTypes.object).isRequired,
    sites: PropTypes.arrayOf(PropTypes.object),
    windowWidth: PropTypes.number.isRequired,
    meta: PropTypes.object,
    getAllGateways: PropTypes.func,
    getAllSites: PropTypes.func,
    deleteGateway: PropTypes.func,
    deleteGatewayFromSite: PropTypes.func,
    isRenderingSiteView: PropTypes.bool,
    isGatewayLoading: PropTypes.bool,
    updateQueryParams: PropTypes.func,
  }

  buildGatewaysQuery = (overrides = {}) => {
    const {
      page,
      perPage,
      online,
      status,
      siteSlug,
      gatewayType,
      searchText,
      sortedInfo,
    } = { ...this.state, ...overrides }
    const sort = buildSortQueryParam(sortedInfo)

    return {
      page,
      perPage,
      online,
      status,
      siteSlug,
      gatewayType,
      ...(searchText ? { filter: searchText } : {}),
      ...(sort ? { sort } : {}),
    }
  }

  componentDidMount() {
    const { getAllGateways, getAllSites, isRenderingSiteView } = this.props

    if (isRenderingSiteView) {
      return
    }

    const {
      page,
      perPage,
      siteSlug,
      online,
      status,
      gatewayType,
      filter,
      sort,
    } = parseQueryParams(global.location)

    const sortedInfo = parseSortFromQuery(sort)
    const initialQuery = {
      page: page ? parseInt(page, 10) : DEFAULT_PAGE,
      perPage: perPage ? parseInt(perPage, 10) : DEFAULT_PER_PAGE,
      online,
      status,
      siteSlug,
      gatewayType,
      searchText: filter || '',
      sortedInfo,
    }

    this.setState(initialQuery)

    getAllGateways &&
      getAllGateways(createQueryString(this.buildGatewaysQuery(initialQuery)))
    getAllSites && getAllSites(createQueryString({ perPage: 'all' }))
  }

  handleChange = (pagination, filters, sorter) => {
    const {
      getAllGateways,
      updateQueryParams,
      isRenderingSiteView,
    } = this.props
    const normalizedSorter = normalizeSorter(sorter)

    if (isRenderingSiteView) {
      this.setState({
        sortedInfo: normalizedSorter.order ? normalizedSorter : {},
      })
      return
    }

    const { page, sortedInfo: prevSortedInfo } = this.state
    const pageChanged = page !== pagination.current
    const sortChanged =
      buildSortQueryParam(prevSortedInfo) !==
      buildSortQueryParam(normalizedSorter)
    const nextPage =
      sortChanged && !pageChanged ? DEFAULT_PAGE : pagination.current
    const nextSortedInfo = normalizedSorter.order
      ? normalizedSorter
      : sortChanged
      ? {}
      : prevSortedInfo

    if (pageChanged || sortChanged) {
      const qs = createQueryString(
        this.buildGatewaysQuery({
          page: nextPage,
          sortedInfo: nextSortedInfo,
        })
      )

      getAllGateways && getAllGateways(qs)

      updateQueryParams && updateQueryParams({ search: qs })
    }

    this.setState({
      page: nextPage,
      sortedInfo: nextSortedInfo,
    })
  }

  handleFilterChange = keyName => value => {
    const {
      getAllGateways,
      updateQueryParams,
      isRenderingSiteView,
    } = this.props

    if (isRenderingSiteView) {
      return
    }

    const newValue =
      value === undefined || (value.length && value.indexOf('all')) > -1
        ? undefined
        : value

    const resetPage = keyName !== 'page' && keyName !== 'perPage'
    const overrides = {
      [keyName]: newValue,
      ...(resetPage ? { page: DEFAULT_PAGE } : {}),
    }

    this.setState(overrides)

    const qs = createQueryString(this.buildGatewaysQuery(overrides))
    getAllGateways(qs)

    updateQueryParams &&
      updateQueryParams({
        search: qs,
      })
  }

  setSearchText = searchText => this.setState({ searchText })

  columnSorter = field => {
    const { isRenderingSiteView } = this.props
    const { sortedInfo } = this.state
    const sortOrder = sortedInfo.field === field ? sortedInfo.order : false

    if (isRenderingSiteView) {
      if (field === 'monitor') {
        return {
          sorter: (a, b) => (a.monitor === b.monitor ? 0 : a.monitor ? -1 : 1),
          sortOrder,
        }
      }

      return {
        sorter: (a, b) => compareFieldValues(a, b, field),
        sortOrder,
      }
    }

    return {
      key: field,
      sorter: true,
      sortOrder,
    }
  }

  render() {
    const {
      deleteGateway,
      deleteGatewayFromSite,
      getAllGateways,
      meta,
      windowWidth,
      isGatewayLoading,
      isRenderingSiteView,
      sites,
      gateways,
    } = this.props
    const {
      searchText,
      perPage,
      online,
      status,
      siteSlug,
      gatewayType,
    } = this.state

    const isFixedColumn = windowWidth < 750

    const isFiltering = !!(
      (online && online.length) ||
      (status && status.length) ||
      (siteSlug && siteSlug.length) ||
      (gatewayType && gatewayType.length)
    )

    const query = this.buildGatewaysQuery()

    return (
      <section className="mb5 AllGateways">
        {!isRenderingSiteView && hasPermission(USER_PERMISSIONS.ADD_GATEWAY) && (
          <div>
            <Anchor to="/inventory/gateways/create" className="mb3" button>
              Create New
            </Anchor>
            <div className="dib red">
              {isFiltering && (
                <p className="pl3">
                  Found {meta.total} gateways matching your criteria
                </p>
              )}
            </div>
          </div>
        )}
        {!isRenderingSiteView && (
          <Fragment>
            <div className="flex-l items-center-l">
              <RadioGroup
                onChange={({ target: { value } }) =>
                  this.handleFilterChange('online')(value)
                }
                value={online}
                className="w-25-l mb3 mr3 mr3-l"
                size="small"
              >
                <RadioButton value={undefined}>All</RadioButton>
                <RadioButton value={CONNECTIVITY_STATUS.ONLINE}>
                  Online
                </RadioButton>
                <RadioButton value={CONNECTIVITY_STATUS.OFFLINE}>
                  Offline
                </RadioButton>
              </RadioGroup>
              <Select
                className="w-third-l mr3-l"
                placeholder="Status"
                mode="multiple"
                input={{
                  value: status,
                  onChange: this.handleFilterChange('status'),
                }}
                filterable
              >
                {['all', ...Object.keys(GATEWAY_STATUS)].map(x => (
                  <Option value={x} key={x}>
                    {toTitleCase(GATEWAY_STATUS[x]) || VIEW_ALL}
                  </Option>
                ))}
              </Select>
              <Select
                className="w-third-l mr3-l"
                placeholder="Network Gateway Type"
                mode="multiple"
                input={{
                  value: gatewayType,
                  onChange: this.handleFilterChange('gatewayType'),
                }}
                filterable
              >
                {['all', ...Object.keys(GATEWAY_TYPES)].map(x => (
                  <Option value={x} key={x}>
                    {toTitleCase(GATEWAY_TYPES[x]) || VIEW_ALL}
                  </Option>
                ))}
              </Select>
              <Select
                className="w-third-l"
                placeholder="Site"
                mode="multiple"
                input={{
                  value: siteSlug,
                  onChange: this.handleFilterChange('siteSlug'),
                }}
                filterable
              >
                {[{ slug: 'all', name: VIEW_ALL }, ...(sites || [])].map(
                  ({ slug, name }) => (
                    <Option value={slug} key={slug}>
                      {name}
                    </Option>
                  )
                )}
              </Select>
            </div>
            <SearchFilter
              searchText={searchText}
              setSearchText={this.setSearchText}
              getItems={getAllGateways}
              query={query}
              id="inventory-gateways-search"
            />
          </Fragment>
        )}
        {isGatewayLoading ? (
          <Spin size="large" className="w-100 center mt5" />
        ) : (
          <Table
            dataSource={gateways}
            onChange={this.handleChange}
            rowKey="id"
            scroll={{ x: 650 }}
            bordered
            pagination={
              !isRenderingSiteView &&
              configurePagination({
                perPage,
                onShowSizeChange: (page, perPage) =>
                  this.handleFilterChange('perPage')(perPage),
                ...meta,
              })
            }
            tableLayout="auto"
          >
            <Column
              title="Pillar ID"
              dataIndex="gatewayPillarId"
              width={150}
              {...this.columnSorter('gatewayPillarId')}
              render={(text, record) => {
                const id = <span className="pl2">{text}</span>

                return record.online ? (
                  <span className="flex items-center">
                    <Tooltip title="Online">
                      <Icon type="check-circle-o" className="green" />{' '}
                      <span className="b">{id}</span>
                    </Tooltip>
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Tooltip title="Offline">
                      <Icon type="poweroff" className="red" /> {id}
                    </Tooltip>
                  </span>
                )
              }}
            />
            <Column
              title="Name"
              dataIndex="gatewayName"
              width={isRenderingSiteView ? 350 : 200}
              {...this.columnSorter('gatewayName')}
              render={(text, record) =>
                hasPermission(USER_PERMISSIONS.VIEW_GATEWAY) ? (
                  <Anchor to={`/inventory/gateways/${record.gatewayPillarId}`}>
                    {text}
                  </Anchor>
                ) : (
                  text
                )
              }
            />
            <Column
              title="Monitoring"
              dataIndex="monitor"
              width={150}
              {...this.columnSorter('monitor')}
              render={text =>
                text ? <span className="b">Active</span> : 'Inactive'
              }
            />
            {!isRenderingSiteView && (
              <Column
                title="Site"
                dataIndex="siteName"
                key="siteName"
                width={150}
                {...this.columnSorter('siteName')}
                render={(text, record) =>
                  text ? (
                    <Anchor to={`/sites/${record.siteSlug}`}>{text}</Anchor>
                  ) : (
                    '--'
                  )
                }
              />
            )}
            {!isRenderingSiteView && (
              <Column
                title="Asset Location"
                dataIndex="assetLocationPillarId"
                width={100}
                {...this.columnSorter('assetLocationPillarId')}
                render={(text, record) =>
                  text ? (
                    <Anchor
                      to={`/inventory/asset-locations/${record.assetLocationPillarId}`}
                    >
                      {text}
                    </Anchor>
                  ) : (
                    '--'
                  )
                }
              />
            )}
            {!isRenderingSiteView && (
              <Column
                title="Status"
                dataIndex="status"
                width={100}
                {...this.columnSorter('status')}
                render={text => toTitleCase(text)}
              />
            )}
            <Column
              title="Type"
              dataIndex="gatewayType"
              width={100}
              {...this.columnSorter('gatewayType')}
              render={text => (text ? toTitleCase(text) : '--')}
            />
            {hasPermission(USER_PERMISSIONS.VIEW_MODEM_AND_CARRIER) && (
              <Column
                title="Modem Type"
                dataIndex="modemTypeName"
                width={100}
                {...this.columnSorter('modemTypeName')}
                render={text => text || '--'}
              />
            )}
            {hasPermission(USER_PERMISSIONS.EDIT_GATEWAY) &&
              (isRenderingSiteView ? (
                <Column
                  fixed={isFixedColumn ? 'right' : false}
                  width={100}
                  render={(text, record) => {
                    const menu = (
                      <Menu>
                        <Menu.Item key="edit-gateway">
                          <Anchor
                            to={`/inventory/gateways/${record.gatewayPillarId}/edit`}
                          >
                            Edit
                          </Anchor>
                        </Menu.Item>
                        <Menu.Item key="delete-gateway">
                          <ActionAnchor
                            onClick={() => deleteGatewayFromSite(record.id)}
                          >
                            Remove
                          </ActionAnchor>
                        </Menu.Item>
                      </Menu>
                    )

                    return (
                      <Dropdown overlay={menu} trigger={['click']}>
                        <a className="ant-dropdown-link">
                          Actions <Icon type="down" />
                        </a>
                      </Dropdown>
                    )
                  }}
                />
              ) : (
                <Column
                  fixed={isFixedColumn ? 'right' : false}
                  width={100}
                  render={(text, record) => {
                    const menu = (
                      <Menu>
                        <Menu.Item key="edit-gateway">
                          <Anchor
                            to={`/inventory/gateways/${record.gatewayPillarId}/edit`}
                          >
                            Edit
                          </Anchor>
                        </Menu.Item>
                        <Menu.Item key="delete-gateway">
                          <ActionAnchor
                            onClick={() => deleteGateway(record.id)}
                          >
                            Delete
                          </ActionAnchor>
                        </Menu.Item>
                      </Menu>
                    )

                    return (
                      <Dropdown overlay={menu} trigger={['click']}>
                        <a className="ant-dropdown-link">
                          Actions <Icon type="down" />
                        </a>
                      </Dropdown>
                    )
                  }}
                />
              ))}
          </Table>
        )}
      </section>
    )
  }
}

export default AllGateways
